/**
 * PDFをアプリ内で表示する(2026-08-31 指示)。
 *
 * スマホのブラウザは <iframe> のPDFを埋め込み表示できず「開く」ボタンだけが出るため、
 * pdf.js で canvas に描画して画面内に表示する(外部アプリに切り替わらない)。
 *
 * 2026-09-10 改修(発注者指示):
 * - 1ページずつ表示し、◁ ▷(いま何ページ目 / 全何ページ)でページを送る(縦スクロールで送らない)
 * - ホイール・ピンチは拡大縮小。＋/− ボタンと今の倍率(%)も出す(「全体」で画面に収まる大きさへ)
 * - 画面いっぱいに表示する(FilePreviewModal 側で全画面にしている)
 *
 * 2026-09-10 修正(請求書PDFが表示できない・白紙になる):
 * - 日本語などのCMap・標準フォント・画像展開のwasm・カラープロファイルの置き場所を渡す
 *   (public/pdfjs。無いと日本語の文字が出ず、スキャン画像のページが白紙になる)
 * - 失敗の理由を画面と開発者コンソールに出し、「別のタブで開く」も用意する
 *
 * 2026-09-16 修正(iPhoneでPDFが見られない。Androidは見られる):
 * - pdf.js の通常版は、iPhoneのSafariにまだ無い命令(Promise.try・Map の getOrInsert・Math.sumPrecise など)を
 *   補完なしで使っている。PDFの読み込みは別スレッド(Web Worker)で動くため、画面側の補完も届かない。
 *   そこで同じ版の「旧端末向けビルド(legacy)」を使う。こちらは本体・Workerとも補完入りで、足りない端末でだけ働く
 * - iPhoneのSafariは1枚の描画領域が約1,677万画素を超えると白紙になるため、上限をその内側に抑える
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ZoomablePane } from './ZoomableImage';
import ViewerControls from './ViewerControls';

/** pdf.js の補助ファイル(tools/copy-pdfjs-assets.mjs で public/pdfjs へ配置) */
export const PDFJS_ASSETS = {
  cMapUrl: '/pdfjs/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/pdfjs/standard_fonts/',
  wasmUrl: '/pdfjs/wasm/',
  iccUrl: '/pdfjs/iccs/',
};

type PdfDoc = {
  numPages: number;
  getPage: (n: number) => Promise<{
    getViewport: (o: { scale: number }) => { width: number; height: number };
    render: (o: { canvas: HTMLCanvasElement; canvasContext: CanvasRenderingContext2D; viewport: unknown }) => RenderTask;
  }>;
  destroy?: () => void;
};

type RenderTask = { promise: Promise<void>; cancel: () => void };

/** 描画を途中で止めたときの例外か(pdf.js の RenderingCancelledException) */
function isRenderCancelled(e: unknown): boolean {
  return (e as { name?: string } | null)?.name === 'RenderingCancelledException';
}

export default function PdfView({ url, maxHeight }: { url: string; maxHeight: string }) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const docRef = useRef<PdfDoc | null>(null);
  /** いま描画中の処理。新しく描く前に止める(同じ canvas への同時描画はエラーになるため。2026-09-16) */
  const renderTaskRef = useRef<RenderTask | null>(null);
  /** 描画の依頼番号。もっと新しい依頼が来ていたら古い依頼は途中でやめる */
  const drawSeq = useRef(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  /** 描き直すときに使う「いまの拡大率」 */
  const zoomRef = useRef(1);
  const [attempt, setAttempt] = useState(0);
  /** 自動でやり直すのは1回だけ(何度も繰り返さない) */
  const autoRetried = useRef(false);
  const fill = maxHeight === '100%';

  // ---- PDFを読み込む ----
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setPages(0);
    setPage(1);
    setZoom(1);
    (async () => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url');
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      const doc = (await pdfjs.getDocument({ url, ...PDFJS_ASSETS }).promise) as unknown as PdfDoc;
      if (cancelled) {
        doc.destroy?.();
        return;
      }
      docRef.current = doc;
      setPages(doc.numPages);
    })().catch((e) => {
      if (cancelled) return;
      console.error('PDFを表示できませんでした', e);
      // 通信の一時的な失敗のことがあるので、1回だけ自動でやり直す
      if (!autoRetried.current) {
        autoRetried.current = true;
        setAttempt((n) => n + 1);
        return;
      }
      setStatus('error');
    });
    return () => {
      cancelled = true;
      // 画面を閉じたら描画を止め、PDFの読み込み用スレッドも片付ける。
      // 残したままだと開くたびに増え、iPhoneではメモリ不足でページが落ちることがある(2026-09-16)
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      docRef.current?.destroy?.();
      docRef.current = null;
    };
  }, [url, attempt]);

  // ---- いま見ているページを描く ----
  // 表示の大きさは「枠に収まる大きさ」で固定し、拡大は transform で行う。
  // ただし canvas の中身は拡大率に合わせて細かく描き直す(2026-09-10: 拡大すると文字が粗くなるため)
  const draw = useCallback(
    async (zoomLevel: number) => {
      const seq = ++drawSeq.current;
      const doc = docRef.current;
      const box = boxRef.current;
      const canvas = canvasRef.current;
      if (!doc || !box || !canvas) return;
      // 前の描画(開いた直後・拡大・画面サイズ変更が重なったとき)を止め、終わるのを待ってから描き直す。
      // 以前は同じ canvas に同時に描こうとしてエラーになっていた(2026-09-16)
      const previous = renderTaskRef.current;
      if (previous) {
        previous.cancel();
        await previous.promise.catch(() => undefined);
      }
      if (seq !== drawSeq.current) return;
      const target = Math.min(Math.max(page, 1), doc.numPages);
      const pdfPage = await doc.getPage(target);
      if (seq !== drawSeq.current) return;
      const base = pdfPage.getViewport({ scale: 1 });
      // 枠(画面)に収まる倍率。文字がぼやけないよう端末解像度と拡大率も掛ける
      const boxW = box.clientWidth || 800;
      const boxH = box.clientHeight || 1000;
      const fitScale = Math.min(boxW / base.width, boxH / base.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let quality = Math.min(Math.max(zoomLevel, 1), 4);
      // 大きくしすぎると端末が耐えられないので、画素数に上限を設ける。
      // iPhoneのSafariは約1,677万画素を超えると白紙になるため、その内側(1,600万)にする(2026-09-16)
      const MAX_PIXELS = 16_000_000;
      const pixels = base.width * fitScale * dpr * quality * (base.height * fitScale * dpr * quality);
      if (pixels > MAX_PIXELS) quality = Math.max(1, quality * Math.sqrt(MAX_PIXELS / pixels));
      const viewport = pdfPage.getViewport({ scale: fitScale * dpr * quality });
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      canvas.style.width = `${Math.floor(base.width * fitScale)}px`;
      canvas.style.height = `${Math.floor(base.height * fitScale)}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const task = pdfPage.render({ canvas, canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      try {
        await task.promise;
      } catch (e) {
        // 新しい描画のために止めた場合はエラーにしない
        if (!isRenderCancelled(e)) throw e;
      } finally {
        if (renderTaskRef.current === task) renderTaskRef.current = null;
      }
    },
    [page],
  );

  // ページを開いたとき・ページを送ったときに描く
  useEffect(() => {
    if (pages === 0) return;
    let cancelled = false;
    draw(zoomRef.current)
      .then(() => {
        if (!cancelled) setStatus('ready');
      })
      .catch((e) => {
        console.warn('このページを描画できませんでした', e);
        if (!cancelled) setStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [pages, draw]);

  // 拡大率が変わったら、その倍率で描き直す(ピンチ中に何度も描かないよう少し待つ)
  useEffect(() => {
    zoomRef.current = zoom;
    if (pages === 0) return;
    const timer = window.setTimeout(() => void draw(zoom).catch(() => undefined), 250);
    return () => window.clearTimeout(timer);
  }, [zoom, pages, draw]);

  // 画面の大きさが変わったら描き直す(向きの変更・ウィンドウのリサイズ)
  useEffect(() => {
    const box = boxRef.current;
    if (!box || pages === 0) return;
    let timer = 0;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void draw(zoomRef.current).catch(() => undefined), 200);
    });
    observer.observe(box);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [pages, draw]);

  // 矢印キーでもページを送れるようにする
  useEffect(() => {
    if (pages <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setPage((p) => Math.max(1, p - 1));
      if (e.key === 'ArrowRight') setPage((p) => Math.min(pages, p + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pages]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0, flex: fill ? 1 : undefined }}>
      <div
        ref={boxRef}
        style={{
          flex: fill ? 1 : undefined,
          maxHeight: fill ? '100%' : maxHeight,
          height: fill ? undefined : maxHeight,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <ZoomablePane
          style={{ width: '100%', height: '100%', minHeight: 0 }}
          contentStyle={{ margin: 'auto', lineHeight: 0 }}
          scale={zoom}
          onScaleChange={setZoom}
        >
          <canvas ref={canvasRef} style={{ display: 'block', background: '#fff' }} />
        </ZoomablePane>
      </div>

      {status === 'loading' && <p className="empty">PDFを読み込み中…</p>}
      {status === 'error' && (
        <p className="empty">
          PDFを表示できませんでした{' '}
          <button
            className="txn-link"
            onClick={() => {
              autoRetried.current = false;
              setAttempt((n) => n + 1);
            }}
          >
            やり直す
          </button>{' '}
          <a className="txn-link" href={url} target="_blank" rel="noopener noreferrer">
            別のタブで開く
          </a>
        </p>
      )}
      {status === 'ready' && (
        <ViewerControls page={page} pages={pages} onPage={(n) => setPage(Math.min(Math.max(n, 1), pages))} zoom={zoom} onZoom={setZoom} />
      )}
    </div>
  );
}
